import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Table, Badge, Spinner, Alert, Input, Row, Col, Button, Container } from 'reactstrap';
import AdminLayout from '../../Components/Admin/AdminLayout';

const AuditLogs = () => {
  const {
    t
  } = useTranslation();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const API_BASE = process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api';
  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE}/admin/audit/logs`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setLogs(data.data || []);
      } else {
        throw new Error(data.error?.message || 'Failed to fetch audit logs');
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);
  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const getActionBadgeColor = action => {
    const colors = {
      invitation_sent: 'primary',
      invitation_accepted: 'success',
      invitation_declined: 'warning',
      invitation_revoked: 'danger',
      invitation_reminded: 'info',
      connection_created: 'success',
      connection_updated: 'info',
      connection_deleted: 'danger',
      card_shared: 'primary',
      profile_viewed: 'secondary',
      user_login: 'success',
      user_logout: 'secondary',
      admin_action: 'dark'
    };
    return colors[action] || 'secondary';
  };

  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatPayload = payload => {
    try {
      const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return payload;
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesSearch = searchTerm === '' || log.who.toLowerCase().includes(searchTerm.toLowerCase()) || log.action.toLowerCase().includes(searchTerm.toLowerCase()) || log.entityId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterAction === 'all' || log.action === filterAction;
    return matchesSearch && matchesFilter;
  });
  const uniqueActions = [...new Set(logs.map(log => log.action))];
  return <AdminLayout>
            <Container fluid className="px-4">
                <Row className="mb-4">
                    <Col>
                        <h2>{t("System Audit Logs")}</h2>
                        <p className="text-muted">{t("View system audit trail for invitation operations and critical actions")}</p>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={6}>
                        <Input type="text" placeholder={t("Search by user, action, or entity ID...")} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                    </Col>
                    <Col md={4}>
                        <Input type="select" value={filterAction} onChange={e => setFilterAction(e.target.value)}>
                            <option value="all">{t("All Actions")}</option>
                            {uniqueActions.map(action => <option key={action} value={action}>{action}</option>)}
                        </Input>
                    </Col>
                    <Col md={2}>
                        <Button color="primary" onClick={fetchAuditLogs} block>{t("Refresh")}</Button>
                    </Col>
                </Row>

                {error && <Alert color="danger">
                        <strong>{t("Error:")}</strong> {error}
                    </Alert>}

                {loading ? <div className="text-center my-5">
                        <Spinner color="primary" />
                        <p className="mt-2">{t("Loading audit logs...")}</p>
                    </div> : <Card>
                        <CardBody>
                            <div className="mb-3">
                                <strong>{t("Total Logs:")}</strong> {filteredLogs.length}
                                {searchTerm || filterAction !== 'all' ? ` (filtered from ${logs.length})` : ''}
                            </div>

                            <div style={{
            overflowX: 'auto'
          }}>
                                <Table striped hover responsive>
                                    <thead>
                                        <tr>
                                            <th>{t("Timestamp")}</th>
                                            <th>{t("Action")}</th>
                                            <th>{t("User")}</th>
                                            <th>{t("Entity")}</th>
                                            <th>{t("Entity ID")}</th>
                                            <th>{t("Details")}</th>
                                            <th>{t("Request ID")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredLogs.length === 0 ? <tr>
                                                <td colSpan="7" className="text-center text-muted">{t("No audit logs found")}</td>
                                            </tr> : filteredLogs.map(log => <tr key={log.id}>
                                                    <td style={{
                    whiteSpace: 'nowrap'
                  }}>
                                                        <small>{formatDate(log.when)}</small>
                                                    </td>
                                                    <td>
                                                        <Badge color={getActionBadgeColor(log.action)}>
                                                            {log.action}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <code>{log.who}</code>
                                                    </td>
                                                    <td>{log.entity}</td>
                                                    <td>
                                                        <code style={{
                      fontSize: '0.85em'
                    }}>{log.entityId}</code>
                                                    </td>
                                                    <td>
                                                        <details>
                                                            <summary style={{
                        cursor: 'pointer'
                      }}>
                                                                <small className="text-muted">{t("View payload")}</small>
                                                            </summary>
                                                            <pre style={{
                        fontSize: '0.75em',
                        maxWidth: '300px',
                        overflow: 'auto',
                        backgroundColor: '#f5f5f5',
                        padding: '8px',
                        borderRadius: '4px',
                        marginTop: '8px'
                      }}>
                                                                {formatPayload(log.payload)}
                                                            </pre>
                                                        </details>
                                                    </td>
                                                    <td>
                                                        <small>
                                                            <code>{log.requestId || 'N/A'}</code>
                                                        </small>
                                                    </td>
                                                </tr>)}
                                    </tbody>
                                </Table>
                            </div>
                        </CardBody>
                    </Card>}
            </Container>
        </AdminLayout>;
};

export default AuditLogs;